import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { ElementRef, Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { filter, Subject, takeUntil } from 'rxjs';

import { popoverPlacement, PopoverPositions, popoverTrigger } from './popover.model';

@Injectable()
export class PopoverService {
  public closedPopover$ = new Subject<OverlayRef>();
  public openedPopover$ = new Subject<OverlayRef>();

  public constructor(
    private overlay: Overlay,
    private scrollDispatcher: ScrollDispatcher,
  ) {}

  public openPopover(
    templateRef: TemplateRef<unknown>,
    elementRef: ElementRef,
    viewContainerRef: ViewContainerRef,
    position: popoverPlacement = 'bottom',
    trigger?: popoverTrigger,
    closable = true,
  ): OverlayRef {
    const scrollableAncestors = this.scrollDispatcher.getAncestorScrollContainers(elementRef);
    const { [position]: _, ...rest } = PopoverPositions;

    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(elementRef)
      .withFlexibleDimensions(false)
      .withScrollableContainers(scrollableAncestors)
      .withPositions([PopoverPositions[position], ...Object.values<ConnectedPosition>(rest)]);

    const elementWidth = elementRef.nativeElement.offsetWidth;
    const elementHeight = elementRef.nativeElement.offsetHeight;
    const isSmallElement = elementWidth < 50 || elementHeight < 50;

    const overlayRef = this.overlay.create({
      hasBackdrop: trigger === 'click' ? true : false,
      backdropClass: '',
      positionStrategy: strategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition({ scrollThrottle: 20 }),
      panelClass: isSmallElement ? 'sb-popover-small-trigger' : '',
    });

    if (closable) {
      this.addCloseListeners(overlayRef);
    }
    this.attachOverlay(overlayRef, templateRef, viewContainerRef);

    this.openedPopover$.next(overlayRef);

    return overlayRef;
  }

  public closePopover(overlayRef: OverlayRef) {
    if (overlayRef?.hasAttached()) {
      overlayRef.detach();
      this.closedPopover$.next(overlayRef);
    }
  }

  private addCloseListeners(overlayRef: OverlayRef) {
    const detachedOverlay$ = this.closedPopover$.pipe(filter((ref) => ref === overlayRef));

    overlayRef
      .backdropClick()
      .pipe(takeUntil(detachedOverlay$))
      .subscribe(() => {
        this.closePopover(overlayRef);
      });

    overlayRef
      .detachments()
      .pipe(takeUntil(detachedOverlay$))
      .subscribe(() => {
        this.closePopover(overlayRef);
      });

    // close on escape
    overlayRef
      .keydownEvents()
      .pipe(takeUntil(detachedOverlay$))
      .subscribe((event) => {
        if (event.keyCode === ESCAPE && !hasModifierKey(event)) {
          this.closePopover(overlayRef);
          event.preventDefault();
          event.stopPropagation();
        }
      });
  }

  private attachOverlay(
    overlayRef: OverlayRef,
    templateRef: TemplateRef<unknown>,
    viewContainerRef: ViewContainerRef,
  ): void {
    if (overlayRef.hasAttached()) {
      return;
    }

    const portal = new TemplatePortal(templateRef, viewContainerRef);
    overlayRef.attach(portal);
  }
}
